import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ViewContainerRef, ComponentRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SelectPanelWidthDirective } from '../../../_helpers/select-panel-width.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DashboardLayoutConfig, DEFAULT_LAYOUT } from 'src/app/models/dashboard-view.model';
import { DashboardViewWidget } from 'src/app/models/dashboard-view-widget.model';
import { DashboardViewWithWidgets } from 'src/app/services/dashboard-view.service';
import { DashboardWidgetType } from 'src/app/enums/dashboardWidgetType.enum';
import { DashboardWidgetDefinition, IWidgetConfigComponent, WidgetCategory, WIDGET_CATEGORIES, WidgetConfigField, WIDGET_SIZE_PRESETS, WIDGET_SIZE_PRESET_LABELS, WidgetSizePreset } from 'src/app/models/dashboard/dashboard-widget.model';

import { WidgetPickerResult } from '../widget-picker-dialog/widget-picker-dialog.component';
import { getAllWidgetDefinitions, getWidgetDefinition, getWidgetTypeString } from '../../../components/dashboard/dashboard-widgets/dashboard-widget-registry';
import { isWidgetTitleCustom, markCustomTitle, preserveTitleConfig, resolveWidgetTitle } from '../../../components/dashboard/dashboard-widgets/widget-title.util';
import { WidgetTitleMeta } from 'src/app/models/dashboard/widget-title.model';
import { GRID_DEFAULTS, COLUMN_OPTIONS, ROW_HEIGHT_PRESETS, GAP_PRESETS, SETTINGS_TABS, SettingsTab } from '../../../components/dashboard/dashboard.constants';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevelShareService } from '../../services/organization-level-share.service';
import { WidgetPickerDialogComponent } from '../widget-picker-dialog/widget-picker-dialog.component';
import { PermissionService } from 'src/app/services/permission.service';
import { TranslateService } from 'src/app/services/translate.service';
import { WidgetOrgFilterComponent, ORG_LEVEL_OVERRIDE_KEY } from 'src/app/common-components/widget-org-filter/widget-org-filter.component';

@Component({
    standalone: true,
    selector: 'app-dashboard-settings-flyout',
    imports: [CommonModule, FormsModule, DragDropModule, TranslateModule, MatFormFieldModule, MatSelectModule, MatCheckboxModule, MatTooltipModule, WidgetOrgFilterComponent, SelectPanelWidthDirective],
    templateUrl: './dashboard-settings-flyout.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./dashboard-settings-flyout.component.scss']
})
export class DashboardSettingsFlyoutComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dashboardView?: DashboardViewWithWidgets;
  @Input() isOpen = false;
  @Input() activeTab: SettingsTab = 'widgets';
  @Input() selectedWidgetInput?: DashboardViewWidget;
  @Input() targetSlotIndex: number | null = null;
  @Input() defaultOrgLevelId?: string;
  @Input() hasPendingChanges = false;

  @Output() close = new EventEmitter<DashboardViewWithWidgets | null>();
  @Output() widgetsChanged = new EventEmitter<{ widgets: DashboardViewWidget[], changedWidgetId?: string }>();
  @Output() layoutChanged = new EventEmitter<DashboardLayoutConfig>();
  @Output() widgetAdded = new EventEmitter<{ widgetType: string; targetSlotIndex?: number | null; position?: { x: number; y: number; width: number; height: number }; configJson?: string }>();
  @Output() widgetRemoved = new EventEmitter<string>();
  @Output() widgetConfigured = new EventEmitter<{ widgetId: string; config: Record<string, unknown> }>();
  @Output() widgetTitleChanged = new EventEmitter<string>();
  @Output() resetToDefault = new EventEmitter<void>();

  currentTab: SettingsTab = SETTINGS_TABS.WIDGETS;
  widgetCategories = WIDGET_CATEGORIES;
  availableWidgets: DashboardWidgetDefinition[] = [];
  filteredWidgets: DashboardWidgetDefinition[] = [];
  widgetSearchQuery = '';
  selectedCategory: WidgetCategory | 'all' = 'all';
  widgetInstances: DashboardViewWidget[] = [];
  widgetIconByWidgetId: Record<string, string> = {};
  widgetDisplayNameByWidgetId: Record<string, string> = {};
  widgetTitleMetaByWidgetId: Record<string, WidgetTitleMeta> = {};
  widgetDescriptionByWidgetId: Record<string, string> = {};
  previewWidgetMap: Record<number, DashboardViewWidget | undefined> = {};
  previewSpanOccupiedMap: Record<number, boolean> = {};
  layoutConfig: DashboardLayoutConfig = { ...DEFAULT_LAYOUT };
  showWidgetPicker = false;

  selectedWidgetId?: string;
  selectedWidget?: DashboardViewWidget;
  selectedWidgetDefinition?: DashboardWidgetDefinition;
  widgetConfig: Record<string, unknown> = {};

  get widgetOrgLevelOverrideId(): string | null {
    return (this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY] as string | null | undefined) ?? null;
  }

  set widgetOrgLevelOverrideId(id: string | null) {
    this.widgetConfig = { ...this.widgetConfig, [ORG_LEVEL_OVERRIDE_KEY]: id };
    this.applyWidgetConfig();
  }

  sizePresets = Object.entries(WIDGET_SIZE_PRESETS).map(([key, value]) => ({
    key: key as WidgetSizePreset,
    label: WIDGET_SIZE_PRESET_LABELS[key as WidgetSizePreset],
    ...value
  }));

  readonly columnOptions = COLUMN_OPTIONS;
  readonly rowHeightPresets = ROW_HEIGHT_PRESETS;
  readonly gapPresets = GAP_PRESETS;
  private viewLevelDirty = false;

  get hasChanges(): boolean {
    return this.hasPendingChanges || this.viewLevelDirty;
  }

  organizationLevels: Lookup[] = [];
  organizationLevelsHierarchical: OrganizationLevel[] = [];
  selectedOrgLevelId?: string | null = null;
  shareView = false;

  private configHostRef?: ViewContainerRef;
  @ViewChild('configHost', { read: ViewContainerRef }) set configHost(ref: ViewContainerRef | undefined) {
    this.configHostRef = ref;
    if (ref && this.selectedWidgetDefinition?.configComponent && !this.configComponentRef) {
      this.renderConfigComponent();
    }
  }
  private configComponentRef?: ComponentRef<IWidgetConfigComponent>;
  private configChangeSub?: Subscription;

  editingTitle = false;
  viewNameEdit = '';

  constructor(
    private orgLevelShareService: OrganizationLevelShareService,
    private dialog: MatDialog,
    private permissionService: PermissionService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.availableWidgets = getAllWidgetDefinitions().filter(
      w => !w.requiredPermission || this.permissionService.hasAllReadPermissions(w.requiredPermission)
    );
    this.filterWidgets();
    this.loadOrganizationLevels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardView'] && this.dashboardView) {
      this.widgetInstances = [...(this.dashboardView.widgets || [])];
      this.layoutConfig = { ...(this.dashboardView.layoutJson || DEFAULT_LAYOUT) };
      this.computeWidgetVms();
    }

    if (changes['activeTab']) {
      this.currentTab = this.activeTab;
    }

    if (changes['isOpen'] && this.isOpen) {
      this.loadFromDashboardView();
      if (this.selectedWidgetInput) {
        this.selectWidgetById(this.selectedWidgetInput.id);
      }
    }

    if (changes['selectedWidgetInput'] && this.selectedWidgetInput && this.isOpen) {
      this.selectWidgetById(this.selectedWidgetInput.id);
    }
  }

  private loadOrganizationLevels(): void {
    this.orgLevelShareService.loadOrganizationLevels().subscribe({
      next: (data) => {
        this.organizationLevels = data.flat;
        this.organizationLevelsHierarchical = data.hierarchical;
      },
      error: () => {
        this.organizationLevels = [];
        this.organizationLevelsHierarchical = [];
      }
    });
  }

  private loadFromDashboardView(): void {
    if (!this.dashboardView) return;

    this.widgetInstances = [...(this.dashboardView.widgets || [])];
    this.layoutConfig = { ...(this.dashboardView.layoutJson || DEFAULT_LAYOUT) };
    this.computeWidgetVms();
    this.shareView = this.dashboardView.isShared || false;
    this.selectedOrgLevelId = this.dashboardView.organizationLevelId ?? this.defaultOrgLevelId ?? null;
    this.viewLevelDirty = false;
    this.clearWidgetSelection();
  }

  private selectWidgetById(id: string): void {
    const widget = this.widgetInstances.find(w => w.id === id);
    if (widget) {
      this.selectWidgetForConfig(widget);
    }
  }

  getWidgetListLayoutClass(): string {
    const count = this.widgetInstances?.length ?? 0;

    if (count <= 5) {
      return '';
    }

    if (count <= 10) {
      return 'two-columns';
    }

    return 'two-columns-scroll';
  }

  setTab(tab: SettingsTab): void {
    this.currentTab = tab;
  }

  filterWidgets(): void {
    let filtered = this.availableWidgets;

    if (this.selectedCategory !== 'all') {
      filtered = filtered.filter(w => w.category === this.selectedCategory);
    }

    if (this.widgetSearchQuery.trim()) {
      const query = this.widgetSearchQuery.toLowerCase();
      filtered = filtered.filter(w =>
        this.translateService.getDynamicTranslation(w.name).toLowerCase().includes(query) ||
        this.translateService.getDynamicTranslation(w.description).toLowerCase().includes(query)
      );
    }

    this.filteredWidgets = filtered;
  }

  onCategoryChange(category: WidgetCategory | 'all'): void {
    this.selectedCategory = category;
    this.filterWidgets();
  }

  onWidgetSearch(): void {
    this.filterWidgets();
  }

  isWidgetEnabled(widgetType: string): boolean {
    return this.widgetInstances.some(w => this.resolveWidgetTypeKey(w) === widgetType && w.visible);
  }

  onWidgetToggle(widget: DashboardViewWidget, event: Event): void {
    const isEnabled = (event.target as HTMLInputElement).checked;
    widget.visible = isEnabled;

    if (isEnabled) {
      // Find position that can fit this widget's size
      const pos = this.findAvailablePositionForWidget(widget.width || 1, widget.height || 1);
      widget.positionX = pos.x;
      widget.positionY = pos.y;
    }

    this.computeWidgetVms();
    this.widgetsChanged.emit({ widgets: this.widgetInstances, changedWidgetId: widget.id });
  }

  getWidgetDefinition(widgetType: string) {
    return getWidgetDefinition(widgetType);
  }

  openWidgetPickerDialog(): void {
    const dialogRef = this.dialog.open(WidgetPickerDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      panelClass: 'widget-picker-dialog-panel'
    });

    dialogRef.afterClosed().subscribe((result: WidgetPickerResult) => {
      if (result?.widget) {
        // Build config JSON for grid widgets
        let configJson: string | undefined;
        if (result.gridConfig) {
          configJson = JSON.stringify(result.gridConfig);
        }

        this.widgetAdded.emit({
          widgetType: result.widget.type,
          targetSlotIndex: this.targetSlotIndex,
          configJson
        });

        setTimeout(() => {
          this.widgetInstances = [...this.widgetInstances];
          this.computeWidgetVms();
        }, 100);
      }
    });
  }

  private findAvailablePositionForWidget(width: number, height: number): { x: number; y: number } {
    const cols = this.layoutConfig?.columns ?? GRID_DEFAULTS.COLUMNS;
    const occupied = new Set<string>();

    // Mark all occupied cells
    this.widgetInstances
      .filter(w => w.visible)
      .forEach(w => {
        for (let dy = 0; dy < (w.height || 1); dy++) {
          for (let dx = 0; dx < (w.width || 1); dx++) {
            occupied.add(`${w.positionX + dx},${w.positionY + dy}`);
          }
        }
      });

    // Find first position where widget fits completely
    for (let y = 0; y < 100; y++) {
      for (let x = 0; x < cols; x++) {
        // Check if widget fits starting at this position
        let fits = true;

        // Check if widget goes beyond grid columns
        if (x + width > cols) {
          fits = false;
        } else {
          // Check all cells the widget would occupy
          for (let dy = 0; dy < height && fits; dy++) {
            for (let dx = 0; dx < width && fits; dx++) {
              if (occupied.has(`${x + dx},${y + dy}`)) {
                fits = false;
              }
            }
          }
        }

        if (fits) {
          return { x, y };
        }
      }
    }

    // If no space found, return bottom-left
    return { x: 0, y: 100 };
  }

  configureWidgetByType(widgetType: string): void {
    const widget = this.widgetInstances.find(w =>
      this.resolveWidgetTypeKey(w) === widgetType && w.visible
    );

    if (widget) {
      this.selectWidgetForConfig(widget);
    }
  }

  getActiveWidgetCount(widgetType: string): number {
    return this.widgetInstances.filter(w =>
      this.resolveWidgetTypeKey(w) === widgetType && w.visible
    ).length;
  }

  addWidget(definition: DashboardWidgetDefinition): void {
    this.widgetAdded.emit({
      widgetType: definition.type,
      targetSlotIndex: this.targetSlotIndex,
      position: {
        x: 0,
        y: this.getNextAvailableRow(),
        width: definition.defaultWidth,
        height: definition.defaultHeight
      }
    });
  }

  private getNextAvailableRow(): number {
    const visible = this.widgetInstances.filter(w => w.visible);
    return visible.length ? Math.max(...visible.map(w => w.positionY + (w.height || 1))) : 0;
  }

  removeWidget(widgetId: string): void {
    const index = this.widgetInstances.findIndex(w => w.id === widgetId);
    if (index === -1) return;

    this.widgetInstances.splice(index, 1);
    this.widgetRemoved.emit(widgetId);
    this.computeWidgetVms();

    if (this.selectedWidgetId === widgetId) {
      this.clearWidgetSelection();
    }
  }

  toggleWidgetVisibility(widget: DashboardViewWidget): void {
    widget.visible = !widget.visible;
    this.computeWidgetVms();
    this.widgetsChanged.emit({ widgets: this.widgetInstances, changedWidgetId: widget.id });
  }

  onWidgetDrop(event: CdkDragDrop<DashboardViewWidget[]>): void {
    moveItemInArray(this.widgetInstances, event.previousIndex, event.currentIndex);

    let currentY = 0;
    this.widgetInstances.forEach(w => {
      w.positionY = currentY;
      currentY += w.height || 1;
    });

    this.computeWidgetVms();
    this.widgetsChanged.emit({ widgets: this.widgetInstances });
  }

  selectWidgetForConfig(widget: DashboardViewWidget): void {
    this.destroyConfigComponent();
    this.selectedWidgetId = widget.id;
    this.selectedWidget = widget;
    this.selectedWidgetDefinition = getWidgetDefinition(this.resolveWidgetTypeKey(widget));
    this.widgetConfig = { ...widget.configJson };
    this.currentTab = SETTINGS_TABS.PROPERTIES;
    if (this.configHostRef && this.selectedWidgetDefinition?.configComponent) {
      this.renderConfigComponent();
    }
  }

  clearWidgetSelection(): void {
    this.destroyConfigComponent();
    this.selectedWidgetId = undefined;
    this.selectedWidget = undefined;
    this.selectedWidgetDefinition = undefined;
    this.widgetConfig = {};
  }

  ngOnDestroy(): void {
    this.destroyConfigComponent();
  }

  private renderConfigComponent(): void {
    this.destroyConfigComponent();
    if (!this.configHostRef || !this.selectedWidgetDefinition?.configComponent) return;

    this.configComponentRef = this.configHostRef.createComponent(this.selectedWidgetDefinition.configComponent);
    this.configComponentRef.instance.config = { ...this.widgetConfig };
    this.configChangeSub = this.configComponentRef.instance.configChange.subscribe(config => {
      const orgOverride = this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY];
      this.widgetConfig = orgOverride != null
        ? { ...config, [ORG_LEVEL_OVERRIDE_KEY]: orgOverride }
        : config;
      this.applyWidgetConfig();
    });
  }

  private destroyConfigComponent(): void {
    this.configChangeSub?.unsubscribe();
    this.configChangeSub = undefined;
    this.configComponentRef?.destroy();
    this.configComponentRef = undefined;
  }

  onConfigFieldChange(field: WidgetConfigField, value: unknown): void {
    this.widgetConfig[field.key] = value;
    this.applyWidgetConfig();
  }

  applyWidgetConfig(): void {
    if (!this.selectedWidget) return;

    const widgetTypeKey = this.resolveWidgetTypeKey(this.selectedWidget);
    this.widgetConfig = preserveTitleConfig(this.selectedWidget.configJson, this.widgetConfig);
    this.selectedWidget.configJson = { ...this.widgetConfig, widgetTypeKey };
    this.widgetConfigured.emit({
      widgetId: this.selectedWidget.id,
      config: this.widgetConfig
    });
  }

  isSizePresetValid(preset: { key: WidgetSizePreset; width: number; height: number }): boolean {
    if (!this.selectedWidgetDefinition) return false;

    const minWidth = this.selectedWidgetDefinition.minWidth ?? 1;
    const minHeight = this.selectedWidgetDefinition.minHeight ?? 1;
    const maxWidth = this.selectedWidgetDefinition.maxWidth ?? this.layoutConfig.columns;
    const maxHeight = this.selectedWidgetDefinition.maxHeight ?? 100;

    return preset.width >= minWidth &&
      preset.width <= maxWidth &&
      preset.height >= minHeight &&
      preset.height <= maxHeight;
  }

  getValidSizePresets(): typeof this.sizePresets {
    return this.sizePresets.filter(preset => this.isSizePresetValid(preset));
  }

  applySizePreset(preset: WidgetSizePreset): void {
    if (!this.selectedWidget || !this.selectedWidgetDefinition) return;

    const size = WIDGET_SIZE_PRESETS[preset];
    const minWidth = this.selectedWidgetDefinition.minWidth ?? 1;
    const minHeight = this.selectedWidgetDefinition.minHeight ?? 1;
    const maxWidth = this.selectedWidgetDefinition.maxWidth ?? this.layoutConfig.columns;
    const maxHeight = this.selectedWidgetDefinition.maxHeight ?? 100;

    const newWidth = Math.max(minWidth, Math.min(size.width, maxWidth));
    const newHeight = Math.max(minHeight, Math.min(size.height, maxHeight));

    this.selectedWidget.width = newWidth;
    this.selectedWidget.height = newHeight;
    this.computeWidgetVms();
    this.widgetsChanged.emit({ widgets: this.widgetInstances, changedWidgetId: this.selectedWidget.id });
  }

  updateWidgetPosition(widget: DashboardViewWidget, axis: 'x' | 'y', delta: number): void {
    const max = axis === 'x' ? this.layoutConfig.columns - (widget.width || 1) : 100;
    const prop = axis === 'x' ? 'positionX' : 'positionY';
    const current = widget[prop] ?? 0;
    widget[prop] = Math.max(0, Math.min(current + delta, max));
    this.computeWidgetVms();
    this.widgetsChanged.emit({ widgets: this.widgetInstances, changedWidgetId: widget.id });
  }

  canUpdateWidgetSize(widget: DashboardViewWidget, dimension: 'width' | 'height', delta: number): boolean {
    const def = getWidgetDefinition(this.resolveWidgetTypeKey(widget));
    if (!def) return false;

    const currentValue = widget[dimension];
    const newValue = currentValue + delta;

    const min = dimension === 'width' ? def.minWidth : def.minHeight;
    const max = dimension === 'width'
      ? (def.maxWidth || this.layoutConfig.columns)
      : (def.maxHeight || 100);

    return newValue >= min && newValue <= max;
  }

  updateWidgetSize(widget: DashboardViewWidget, dimension: 'width' | 'height', delta: number): void {
    const def = getWidgetDefinition(this.resolveWidgetTypeKey(widget));
    if (!def) return;

    const min = dimension === 'width' ? def.minWidth : def.minHeight;
    const max = dimension === 'width'
      ? (def.maxWidth || this.layoutConfig.columns)
      : (def.maxHeight || 100);

    const newValue = Math.max(min, Math.min(widget[dimension] + delta, max));
    if (newValue !== widget[dimension]) {
      widget[dimension] = newValue;
      this.computeWidgetVms();
      this.widgetsChanged.emit({ widgets: this.widgetInstances, changedWidgetId: widget.id });
    }
  }

  onLayoutChange(): void {
    this.computeWidgetVms();
    this.layoutChanged.emit(this.layoutConfig);
  }

  getSelectedRowHeightPreset(): string {
    return this.rowHeightPresets.find(p => p.value === this.layoutConfig.rowHeight)?.key || 'normal';
  }

  getSelectedGapPreset(): string {
    return this.gapPresets.find(p => p.value === this.layoutConfig.gap)?.key || 'medium';
  }

  getPreviewRowHeight(): string {
    const scaleFactor = 4;
    const previewHeight = this.layoutConfig.rowHeight * scaleFactor;
    return `${previewHeight}px`;
  }

  getPreviewGap(): string {
    const gapScale = 0.5;
    const previewGap = this.layoutConfig.gap * gapScale;
    return `${previewGap}px`;
  }

  onColumnChange(columns: number): void {
    this.layoutConfig.columns = columns;
    this.onLayoutChange();
  }

  onRowHeightPresetChange(presetKey: string): void {
    const preset = this.rowHeightPresets.find(p => p.key === presetKey);
    if (preset) {
      this.layoutConfig.rowHeight = preset.value;
      this.onLayoutChange();
    }
  }

  onGapPresetChange(presetKey: string): void {
    const preset = this.gapPresets.find(p => p.key === presetKey);
    if (preset) {
      this.layoutConfig.gap = preset.value;
      this.onLayoutChange();
    }
  }

  onWidgetTitleChange(widget: DashboardViewWidget, title: string): void {
    const trimmedTitle = title.trim() || undefined;
    widget.title = trimmedTitle;

    const isCustom = !!trimmedTitle;
    if (isWidgetTitleCustom(widget.configJson) !== isCustom) {
      widget.configJson = markCustomTitle(widget.configJson, isCustom);
    }

    this.viewLevelDirty = true;
    this.widgetTitleChanged.emit(widget.id);
    this.computeWidgetVms();
  }

  toggleShareView(): void {
    this.shareView = !this.shareView;
    const result = this.orgLevelShareService.handleShareToggle(this.shareView, this.selectedOrgLevelId);
    if (!this.shareView) this.selectedOrgLevelId = result.orgLevelId;
    this.viewLevelDirty = true;
  }

  onShareToggle(checked: boolean): void {
    const result = this.orgLevelShareService.handleShareToggle(checked, this.selectedOrgLevelId);
    if (!checked) this.selectedOrgLevelId = result.orgLevelId;
    this.viewLevelDirty = true;
  }

  onOrgLevelChange(): void {
    this.viewLevelDirty = true;
  }

  startEditingTitle(): void {
    if (!this.dashboardView) return;
    this.viewNameEdit = this.dashboardView.viewName;
    this.editingTitle = true;
  }

  cancelEditingTitle(): void {
    this.editingTitle = false;
    this.viewNameEdit = '';
  }

  saveViewName(): void {
    const newName = this.viewNameEdit.trim();
    if (!newName || !this.dashboardView) {
      this.cancelEditingTitle();
      return;
    }

    this.dashboardView.viewName = newName;
    this.viewLevelDirty = true;
    this.editingTitle = false;
  }

  applyPendingState(): DashboardViewWithWidgets | null {
    if (!this.dashboardView) return null;
    this.dashboardView.widgets = this.widgetInstances;
    this.dashboardView.layoutJson = this.layoutConfig;
    this.dashboardView.isShared = this.shareView;
    this.dashboardView.organizationLevelId = this.selectedOrgLevelId ?? this.defaultOrgLevelId;
    return this.dashboardView;
  }

  saveAndClose(): void {
    const view = this.applyPendingState();
    this.currentTab = SETTINGS_TABS.WIDGETS;
    this.close.emit(view);
  }

  discardAndClose(): void {
    this.currentTab = SETTINGS_TABS.WIDGETS;
    this.close.emit(null);
  }

  onResetToDefault(): void {
    this.resetToDefault.emit();
  }

  get previewRows(): number {
    const visible = this.widgetInstances.filter(w => w.visible);
    if (!visible.length) return GRID_DEFAULTS.ROWS;
    return Math.max(GRID_DEFAULTS.ROWS, Math.max(...visible.map(w => w.positionY + (w.height || 1))));
  }

  get previewCells(): number[] {
    return Array.from({ length: this.layoutConfig.columns * this.previewRows }, (_, i) => i);
  }

  getPreviewWidgetAt(cellIndex: number): DashboardViewWidget | undefined {
    const cellX = cellIndex % this.layoutConfig.columns;
    const cellY = Math.floor(cellIndex / this.layoutConfig.columns);
    return this.widgetInstances.find(w => w.visible && w.positionX === cellX && w.positionY === cellY);
  }

  isPreviewCellOccupiedBySpan(cellIndex: number): boolean {
    const cellX = cellIndex % this.layoutConfig.columns;
    const cellY = Math.floor(cellIndex / this.layoutConfig.columns);

    return this.widgetInstances.some(w => {
      if (!w.visible || (w.positionX === cellX && w.positionY === cellY)) return false;
      return cellX >= w.positionX &&
        cellX < w.positionX + (w.width || 1) &&
        cellY >= w.positionY &&
        cellY < w.positionY + (w.height || 1);
    });
  }

  getWidgetIcon(widget: DashboardViewWidget): string {
    return getWidgetDefinition(this.resolveWidgetTypeKey(widget))?.icon || 'widgets';
  }

  getWidgetTypeString(widgetType: DashboardWidgetType): string {
    return getWidgetTypeString(widgetType);
  }

  resolveWidgetTypeKey(widget: DashboardViewWidget): string {
    return widget.configJson?.widgetTypeKey || widget.configJson?._widgetTypeKey || getWidgetTypeString(widget.widgetType);
  }

  getCategoryIcon(category: WidgetCategory | 'all'): string {
    if (category === 'all') return 'apps';
    return WIDGET_CATEGORIES.find(c => c.key === category)?.icon || 'widgets';
  }

  shouldShowConfigField(field: WidgetConfigField): boolean {
    if (!field.showWhen) return true;
    return this.widgetConfig[field.showWhen.field] === field.showWhen.value;
  }

  trackByWidgetType(_: number, widget: DashboardWidgetDefinition): string {
    return widget.type;
  }

  trackByWidgetId(_: number, widget: DashboardViewWidget): string {
    return widget.id;
  }

  trackByCellIndex(index: number): number {
    return index;
  }

  trackByPresetKey(_: number, preset: { key: string }): string {
    return preset.key;
  }

  trackByFieldKey(_: number, field: WidgetConfigField): string {
    return field.key;
  }

  public trackById = trackById;
  public trackByIndex = trackByIndex;

  private computeWidgetVms(): void {
    const iconMap: Record<string, string> = {};
    const displayNameMap: Record<string, string> = {};
    const titleMetaMap: Record<string, WidgetTitleMeta> = {};
    const descriptionMap: Record<string, string> = {};
    this.widgetInstances.forEach(w => {
      const typeKey = this.resolveWidgetTypeKey(w);
      const { widgetTitle, titleMeta } = resolveWidgetTitle(w, typeKey);
      iconMap[w.id] = this.getWidgetIcon(w);
      displayNameMap[w.id] = widgetTitle;
      titleMetaMap[w.id] = titleMeta;
      descriptionMap[w.id] = getWidgetDefinition(typeKey)?.description || '';
    });
    this.widgetIconByWidgetId = iconMap;
    this.widgetDisplayNameByWidgetId = displayNameMap;
    this.widgetTitleMetaByWidgetId = titleMetaMap;
    this.widgetDescriptionByWidgetId = descriptionMap;
    this.computePreviewWidgetMap();
  }

  private computePreviewWidgetMap(): void {
    const map: Record<number, DashboardViewWidget | undefined> = {};
    const spanMap: Record<number, boolean> = {};
    const columns = this.layoutConfig.columns;
    const rows = this.previewRows;
    for (let i = 0; i < columns * rows; i++) {
      const cellX = i % columns;
      const cellY = Math.floor(i / columns);
      map[i] = this.widgetInstances.find(w => w.visible && w.positionX === cellX && w.positionY === cellY);
      spanMap[i] = this.widgetInstances.some(w => {
        if (!w.visible || (w.positionX === cellX && w.positionY === cellY)) return false;
        return cellX >= w.positionX &&
          cellX < w.positionX + (w.width || 1) &&
          cellY >= w.positionY &&
          cellY < w.positionY + (w.height || 1);
      });
    }
    this.previewWidgetMap = map;
    this.previewSpanOccupiedMap = spanMap;
  }
}